// src/pages/stock/AddIssuestock.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaSave, FaTimes, FaBoxes, FaArrowLeft } from 'react-icons/fa';
import Swal from 'sweetalert2';
import api from '../../services/api';
import './Issuestock.css';

const AddIssuestock = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location.state?.editData;
  const isEditMode = !!editData;

  // Form fields state
  const [entryDate, setEntryDate] = useState('');
  const [internalLotNumber, setInternalLotNumber] = useState('');
  const [internalSupplierNumber, setInternalSupplierNumber] = useState(''); // ✅ NEW STATE
  const [material, setMaterial] = useState('');
  const [issuedQuantity, setIssuedQuantity] = useState('');
  const [usedQuantity, setUsedQuantity] = useState('');
  const [returnedQuantity, setReturnedQuantity] = useState('');
  const [remainingStock, setRemainingStock] = useState('');
  const [issueBy, setIssueBy] = useState('');
  const [receivedBy, setReceivedBy] = useState('');
  const [status, setStatus] = useState('');
  const [remarks, setRemarks] = useState('');
  const [barLength, setBarLength] = useState('');
  const [partManufacturingWithoutWastage, setPartManufacturingWithoutWastage] = useState('');
  const [wastageBar, setWastageBar] = useState('');
  const [wastageEachParts, setWastageEachParts] = useState('');
  const [totalWastageMaterial, setTotalWastageMaterial] = useState('');
  const [finalBarUsed, setFinalBarUsed] = useState('');

  // Shop and City state
  const [shops, setShops] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedShop, setSelectedShop] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchShops();
  }, []);

  useEffect(() => {
    if (selectedShop) {
      fetchCities(selectedShop);
    } else {
      setCities([]);
    }
  }, [selectedShop]);

  useEffect(() => {
    if (isEditMode && editData) {
      setEntryDate(editData.entry_date || '');
      setInternalLotNumber(editData.internal_lot_number || '');
      setInternalSupplierNumber(editData.internal_supplier_number || ''); // ✅ NEW EDIT HANDLING
      setMaterial(editData.material || '');
      setIssuedQuantity(editData.issued_quantity || '');
      setUsedQuantity(editData.used_quantity || '');
      setReturnedQuantity(editData.returned_quantity || '');
      setRemainingStock(editData.remaining_stock || '');
      setIssueBy(editData.issue_by || '');
      setReceivedBy(editData.received_by || '');
      setStatus(editData.status || '');
      setRemarks(editData.remarks || '');
      setBarLength(editData.bar_length || '');
      setPartManufacturingWithoutWastage(editData.part_manufacturing_without_wastage || '');
      setWastageBar(editData.wastage_bar || '');
      setWastageEachParts(editData.wastage_each_parts || '');
      setTotalWastageMaterial(editData.total_wastage_material || '');
      setFinalBarUsed(editData.final_bar_used || '');
      setSelectedShop(editData.shop_id || '');
      setSelectedCity(editData.city || '');
    }
  }, [isEditMode, editData]);

  const fetchShops = async () => {
    try {
      const response = await api.get('/user/shops/');
      setShops(response.data);
    } catch (error) {
      console.error('Error fetching shops:', error);
    }
  };

  const fetchCities = async (shopId) => {
    try {
      const response = await api.get(`/inventory/shop-cities/?shop_id=${shopId}`);
      setCities(response.data.cities || []);
    } catch (error) {
      console.error('Error fetching cities:', error);
      setCities([]);
    }
  };

  // ✅ Remaining Stock = Issued - Returned (Used is NOT part of formula)
  useEffect(() => {
    if (issuedQuantity && returnedQuantity) {
      const issued = parseFloat(issuedQuantity) || 0;
      const returned = parseFloat(returnedQuantity) || 0;
      const remaining = issued - returned;
      setRemainingStock(remaining.toString());
    } else if (issuedQuantity) {
      const issued = parseFloat(issuedQuantity) || 0;
      setRemainingStock(issued.toString());
    } else {
      setRemainingStock('');
    }
  }, [issuedQuantity, returnedQuantity]);

  useEffect(() => {
    if (wastageBar && wastageEachParts) {
      const wastage = parseFloat(wastageBar) || 0;
      const eachParts = parseFloat(wastageEachParts) || 0;
      const total = wastage * eachParts;
      setTotalWastageMaterial(total.toString());
    } else if (wastageBar) {
      setTotalWastageMaterial(wastageBar);
    } else {
      setTotalWastageMaterial('');
    }
  }, [wastageBar, wastageEachParts]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ✅ Added validation for internal_supplier_number
    if (!internalLotNumber || !internalSupplierNumber || !material || !issuedQuantity) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Fields',
        text: 'Please fill in all required fields (Internal Lot Number, Internal Supplier Number, Material, Issued Quantity)',
        confirmButtonColor: '#004aad',
      });
      return;
    }

    if (!selectedShop) {
      Swal.fire({
        icon: 'warning',
        title: 'Shop Required',
        text: 'Please select a shop',
        confirmButtonColor: '#004aad',
      });
      return;
    }

    const formData = {
      entry_date: entryDate || new Date().toISOString().split('T')[0],
      internal_lot_number: internalLotNumber,
      internal_supplier_number: internalSupplierNumber, // ✅ ADDED TO FORMDATA
      material: material,
      issued_quantity: parseInt(issuedQuantity),
      used_quantity: parseInt(usedQuantity) || 0,
      returned_quantity: parseInt(returnedQuantity) || 0,
      remaining_stock: parseInt(remainingStock) || 0,
      issue_by: issueBy,
      received_by: receivedBy,
      status: status,
      remarks: remarks,
      bar_length: parseInt(barLength) || 0,
      part_manufacturing_without_wastage: parseInt(partManufacturingWithoutWastage) || 0,
      wastage_bar: parseInt(wastageBar) || 0,
      wastage_each_parts: parseFloat(wastageEachParts) || 0,
      total_wastage_material: parseFloat(totalWastageMaterial) || 0,
      final_bar_used: parseFloat(finalBarUsed) || 0,
      shop_id: selectedShop,
      city: selectedCity || null,
    };

    try {
      setLoading(true);

      if (isEditMode) {
        await api.put(`/inventory/materials/${editData.id}/update/`, formData);
        Swal.fire({
          icon: 'success',
          title: 'Updated!',
          text: 'Issue stock record updated successfully!',
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        await api.post('/inventory/materials/', formData);
        Swal.fire({
          icon: 'success',
          title: 'Saved!',
          text: 'Issue stock record saved successfully!',
          timer: 2000,
          showConfirmButton: false,
        });
      }

      setLoading(false);
      setTimeout(() => {
        navigate('/Issuestock');
      }, 2000);
    } catch (error) {
      setLoading(false);
      console.error('Error saving issue stock:', error);

      if (error.response && error.response.data) {
        const errorData = error.response.data;
        let errorMessage = 'Failed to save record. ';

        if (typeof errorData === 'object') {
          Object.keys(errorData).forEach(key => {
            errorMessage += `\n${key}: ${errorData[key]}`;
          });
        } else {
          errorMessage += errorData;
        }

        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: errorMessage,
          confirmButtonColor: '#dc3545',
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to save record. Please try again.',
          confirmButtonColor: '#dc3545',
        });
      }
    }
  };

  const handleReset = () => {
    Swal.fire({
      title: 'Reset Form?',
      text: 'Are you sure you want to reset the form? All entered data will be cleared.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#004aad',
      cancelButtonColor: '#dc3545',
      confirmButtonText: 'Yes, reset it!',
      cancelButtonText: 'Cancel',
    }).then((result) => {
      if (result.isConfirmed) {
        setEntryDate('');
        setInternalLotNumber('');
        setInternalSupplierNumber(''); // ✅ RESET NEW FIELD
        setMaterial('');
        setIssuedQuantity('');
        setUsedQuantity('');
        setReturnedQuantity('');
        setRemainingStock('');
        setIssueBy('');
        setReceivedBy('');
        setStatus('');
        setRemarks('');
        setBarLength('');
        setPartManufacturingWithoutWastage('');
        setWastageBar('');
        setWastageEachParts('');
        setTotalWastageMaterial('');
        setFinalBarUsed('');
        setSelectedShop('');
        setSelectedCity('');

        Swal.fire({
          icon: 'success',
          title: 'Reset!',
          text: 'Form has been reset successfully.',
          timer: 1500,
          showConfirmButton: false,
        });
      }
    });
  };

  const handleCancel = () => {
    navigate('/Issuestock');
  };

  const handleBack = () => {
    navigate('/Issuestock');
  };

  if (loading) {
    return <div className="loading">Saving...</div>;
  }

  return (
    <div className="add-issuestock-container add-issue-page">
      <div className="form-header-section">
        <div className="header-content">
          <div>
            <h2>{isEditMode ? 'Edit Issue Stock Record' : 'Add Issue Stock Record'}</h2>
          </div>
        </div>

        <div className="back-button-wrapper">
          <button className="back-button" onClick={handleBack}>
            Back
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="issuestock-form-container">
        {/* Section 1: Shop & City */}
        <div className="form-section">
          <h3 className="section-title">Shop Information</h3>

          <div className="ais-grid">
            <div className="form-group ais-field">
              <label>Shop <span className="required">*</span></label>
              <select
                value={selectedShop}
                onChange={(e) => setSelectedShop(e.target.value)}
                required
                className="form-input"
              >
                <option value="">Select Shop</option>
                {shops.map(shop => (
                  <option key={shop.shop_id} value={shop.shop_id}>
                    {shop.shop_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group ais-field">
              <label>City</label>
              <select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                className="form-input"
                disabled={!selectedShop}
              >
                <option value="">Select City</option>
                {cities.map(city => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Section 2: Basic Information — 3 fields per row */}
        <div className="form-section">
          <h3 className="section-title">Basic Information</h3>

          <div className="ais-grid">
            <div className="form-group ais-field">
              <label>Entry Date</label>
              <input
                type="date"
                value={entryDate}
                onChange={(e) => setEntryDate(e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group ais-field">
              <label>Internal Lot Number <span className="required">*</span></label>
              <input
                type="text"
                value={internalLotNumber}
                onChange={(e) => setInternalLotNumber(e.target.value)}
                placeholder="Enter internal lot number"
                required
                className="form-input"
              />
            </div>
            <div className="form-group ais-field">
              <label>Internal Supplier Number <span className="required">*</span></label>
              <input
                type="text"
                value={internalSupplierNumber}
                onChange={(e) => setInternalSupplierNumber(e.target.value)}
                placeholder="Enter internal supplier number"
                required
                className="form-input"
              />
            </div>

            <div className="form-group ais-field">
              <label>Material <span className="required">*</span></label>
              <input
                type="text"
                value={material}
                onChange={(e) => setMaterial(e.target.value)}
                placeholder="Enter material name"
                required
                className="form-input"
              />
            </div>
            <div className="form-group ais-field">
              <label>Issued Quantity <span className="required">*</span></label>
              <input
                type="number"
                step="1"
                value={issuedQuantity}
                onChange={(e) => setIssuedQuantity(e.target.value)}
                placeholder="Enter issued quantity"
                required
                className="form-input"
              />
            </div>
            <div className="form-group ais-field">
              <label>Used Quantity</label>
              <input
                type="number"
                step="1"
                value={usedQuantity}
                onChange={(e) => setUsedQuantity(e.target.value)}
                placeholder="Enter used quantity"
                className="form-input"
              />
            </div>

            <div className="form-group ais-field">
              <label>Returned Quantity</label>
              <input
                type="number"
                step="1"
                value={returnedQuantity}
                onChange={(e) => setReturnedQuantity(e.target.value)}
                placeholder="Enter returned quantity"
                className="form-input"
              />
            </div>
            {/* Remaining Stock */}
            <div className="form-group ais-field">
              <label>Remaining Stock</label>
              <input
                type="text"
                value={remainingStock}
                className="form-input remaining-stock"
                readOnly
                disabled
                placeholder="Auto-calculated"
              />
            </div>
          </div>
        </div>

        {/* Section 3: Personnel Information — 3 fields per row */}
        <div className="form-section">
          <h3 className="section-title">Personnel Information</h3>

          <div className="ais-grid">
            <div className="form-group ais-field">
              <label>Issue By</label>
              <input
                type="text"
                value={issueBy}
                onChange={(e) => setIssueBy(e.target.value)}
                placeholder="Enter name of person issuing"
                className="form-input"
              />
            </div>
            <div className="form-group ais-field">
              <label>Received By</label>
              <input
                type="text"
                value={receivedBy}
                onChange={(e) => setReceivedBy(e.target.value)}
                placeholder="Enter name of person receiving"
                className="form-input"
              />
            </div>
            <div className="form-group ais-field">
              <label>Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="form-input"
              >
                <option value="">Select status</option>
                <option value="Pending">Pending</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>

            <div className="form-group ais-field ais-full">
              <label>Remarks</label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Enter any remarks or additional information"
                rows="3"
                className="form-textarea"
              />
            </div>
          </div>
        </div>

        {/*/!* Section 4: Bar & Wastage Information *!/*/}
        {/*<div className="form-section">*/}
        {/*  <h3 className="section-title">Bar & Wastage Information</h3>*/}

        {/*  <div className="form-row">*/}
        {/*    <div className="form-group">*/}
        {/*      <label>Bar Length (mm)</label>*/}
        {/*      <input*/}
        {/*        type="number"*/}
        {/*        step="1"*/}
        {/*        value={barLength}*/}
        {/*        onChange={(e) => setBarLength(e.target.value)}*/}
        {/*        placeholder="Enter bar length in mm"*/}
        {/*        className="form-input"*/}
        {/*      />*/}
        {/*    </div>*/}
        {/*    <div className="form-group">*/}
        {/*      <label>Part Manufacturing Without Wastage</label>*/}
        {/*      <input*/}
        {/*        type="number"*/}
        {/*        step="1"*/}
        {/*        value={partManufacturingWithoutWastage}*/}
        {/*        onChange={(e) => setPartManufacturingWithoutWastage(e.target.value)}*/}
        {/*        placeholder="Enter parts manufactured without wastage"*/}
        {/*        className="form-input"*/}
        {/*      />*/}
        {/*    </div>*/}
        {/*  </div>*/}

        {/*  <div className="form-row">*/}
        {/*    <div className="form-group">*/}
        {/*      <label>Wastage Bar (mm)</label>*/}
        {/*      <input*/}
        {/*        type="number"*/}
        {/*        step="1"*/}
        {/*        value={wastageBar}*/}
        {/*        onChange={(e) => setWastageBar(e.target.value)}*/}
        {/*        placeholder="Enter wastage bar in mm"*/}
        {/*        className="form-input"*/}
        {/*      />*/}
        {/*    </div>*/}
        {/*    <div className="form-group">*/}
        {/*      <label>Wastage Each Parts</label>*/}
        {/*      <input*/}
        {/*        type="number"*/}
        {/*        step="0.01"*/}
        {/*        value={wastageEachParts}*/}
        {/*        onChange={(e) => setWastageEachParts(e.target.value)}*/}
        {/*        placeholder="Enter wastage per part"*/}
        {/*        className="form-input"*/}
        {/*      />*/}
        {/*    </div>*/}
        {/*  </div>*/}

        {/*  <div className="form-row">*/}
        {/*    <div className="form-group">*/}
        {/*      <label>Total Wastage Material</label>*/}
        {/*      <input*/}
        {/*        type="text"*/}
        {/*        value={totalWastageMaterial}*/}
        {/*        className="form-input remaining-stock"*/}
        {/*        readOnly*/}
        {/*        disabled*/}
        {/*        placeholder="Auto-calculated"*/}
        {/*      />*/}
        {/*    </div>*/}
        {/*    <div className="form-group">*/}
        {/*      <label>Final Bar Used</label>*/}
        {/*      <input*/}
        {/*        type="number"*/}
        {/*        step="0.01"*/}
        {/*        value={finalBarUsed}*/}
        {/*        onChange={(e) => setFinalBarUsed(e.target.value)}*/}
        {/*        placeholder="Enter final bar used"*/}
        {/*        className="form-input"*/}
        {/*      />*/}
        {/*    </div>*/}
        {/*  </div>*/}
        {/*</div>*/}

        <div className="form-actions">

          <button type="button" className="btn-cancel" onClick={handleCancel}>
          Cancel
          </button>
          <button type="submit" className="btn-save" disabled={loading}>
            {isEditMode ? 'Update Record' : 'Save Record'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddIssuestock;